import {
  createBrowserRouter,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { paths } from '~shared/config/paths';
import { useUrlLocale } from '~shared/hooks/use-url-locale';
import { Helmet } from 'react-helmet-async';
import { useSuspenseConfiguration } from '~entities/configuration/api/get-configuration';
import TagManager from 'react-gtm-module';

// https://github.com/remix-run/react-router/discussions/10166
const BubbleError = (): null => {
  const error = useRouteError();

  if (error) throw error;
  return null;
};

// TODO: React router v7 needs HydrateFallback additionally

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;

  return {
    ...rest,
    loader: clientLoader ? clientLoader(queryClient) : undefined,
    action: clientAction ? clientAction(queryClient) : undefined,
    Component,
  };
};

const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      errorElement: <BubbleError />,
      children: [
        {
          lazy: () =>
            import('~app/layouts/main-layout').then(convert(queryClient)),
          children: [
            {
              ...paths.home,
              lazy: () => import('~pages/home-page').then(convert(queryClient)),
            },
            {
              ...paths.packages,
              lazy: () => import('~pages/home-page').then(convert(queryClient)),
            },
            {
              ...paths.packages.details,
              lazy: () =>
                import('~pages/package-details').then(convert(queryClient)),
            },
            {
              ...paths.lastMinutePackages,
              lazy: () => import('~pages/home-page').then(convert(queryClient)),
            },
            {
              ...paths.compare,
              lazy: () =>
                import('~pages/compare-page').then(convert(queryClient)),
            },
            {
              ...paths.paymentSuccess,
              lazy: () =>
                import('~pages/payment-result-page').then(convert(queryClient)),
              handle: () => ({
                status: 'success',
              }),
            },
            {
              ...paths.paymentError,
              lazy: () =>
                import('~pages/payment-result-page').then(convert(queryClient)),
              handle: () => ({
                status: 'error',
              }),
            },
            {
              ...paths.inquirySuccess,
              lazy: () =>
                import('~pages/inquiry-result-page').then(convert(queryClient)),
              handle: () => ({
                status: 'success',
              }),
            },
            {
              ...paths.inquiryError,
              lazy: () =>
                import('~pages/inquiry-result-page').then(convert(queryClient)),
              handle: () => ({
                status: 'error',
              }),
            },
          ],
        },
        {
          lazy: () =>
            import('~processes/booking/ui/booking.layout').then(
              convert(queryClient),
            ),
          children: [
            {
              ...paths.booking,
              lazy: () =>
                import('~pages/booking-page').then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.addVariant.path,
          lazy: async () => {
            const loader = await import(
              '~features/session/add-variant/lib/add-variant.loader'
            ).then(module => module.addVariantLoader);

            return { loader };
          },
        },
        {
          path: paths.addRoom.path,
          lazy: async () => {
            const loader = await import(
              '~features/session/add-room/lib/add-room.loader'
            ).then(module => module.addRoomLoader);

            return { loader };
          },
        },
        {
          path: '*',
          lazy: () => import('~pages/not-found').then(convert(queryClient)),
        },
      ],
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const { data: configuration } = useSuspenseConfiguration();
  const { pageCustomization } = configuration;
  const {
    hotelName,
    logoUrl,
    primaryColor,
    primaryForegroundColor,
    secondaryColor,
    secondaryForegroundColor,
    mutedColor,
    mutedForegroundColor,
    accentColor,
    accentForegroundColor,
    radius,
    googleTagManagerTag,
    calendarPastBackground,
    calendarPastForeground,
    calendarBlockedBackground,
    calendarBlockedForeground,
    calendarArrivalNotPossibleBackground,
    calendarArrivalNotPossibleForeground,
    calendarAvailableBackground,
    calendarAvailableForeground,
  } = pageCustomization;

  useEffect(() => {
    const style = document.documentElement.style;

    primaryColor && style.setProperty('--primary', primaryColor);
    primaryForegroundColor &&
      style.setProperty('--primary-foreground', primaryForegroundColor);
    secondaryColor && style.setProperty('--secondary', secondaryColor);
    secondaryForegroundColor &&
      style.setProperty('--secondary-foreground', secondaryForegroundColor);
    mutedColor && style.setProperty('--muted', mutedColor);
    mutedForegroundColor &&
      style.setProperty('--muted-foreground', mutedForegroundColor);
    accentColor && style.setProperty('--accent', accentColor);
    accentForegroundColor &&
      style.setProperty('--accent-foreground', accentForegroundColor);
    radius && style.setProperty('--radius', radius);
    calendarPastBackground &&
      style.setProperty('--calendar-past-background', calendarPastBackground);
    calendarPastForeground &&
      style.setProperty('--calendar-past-foreground', calendarPastForeground);
    calendarBlockedBackground &&
      style.setProperty(
        '--calendar-blocked-background',
        calendarBlockedBackground,
      );
    calendarBlockedForeground &&
      style.setProperty(
        '--calendar-blocked-foreground',
        calendarBlockedForeground,
      );
    calendarArrivalNotPossibleBackground &&
      style.setProperty(
        '--calendar-arrival-not-possible-background',
        calendarArrivalNotPossibleBackground,
      );
    calendarArrivalNotPossibleForeground &&
      style.setProperty(
        '--calendar-arrival-not-possible-foreground',
        calendarArrivalNotPossibleForeground,
      );
    calendarAvailableBackground &&
      style.setProperty(
        '--calendar-available-background',
        calendarAvailableBackground,
      );
    calendarAvailableForeground &&
      style.setProperty(
        '--calendar-available-foreground',
        calendarAvailableForeground,
      );
  }, [
    accentColor,
    accentForegroundColor,
    mutedColor,
    mutedForegroundColor,
    primaryColor,
    primaryForegroundColor,
    radius,
    secondaryColor,
    secondaryForegroundColor,
    calendarPastBackground,
    calendarPastForeground,
    calendarBlockedBackground,
    calendarBlockedForeground,
    calendarArrivalNotPossibleBackground,
    calendarArrivalNotPossibleForeground,
    calendarAvailableBackground,
    calendarAvailableForeground,
  ]);

  useEffect(() => {
    googleTagManagerTag &&
      TagManager.initialize({
        gtmId: googleTagManagerTag,
      });
  }, [googleTagManagerTag]);

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  useUrlLocale(router);

  return (
    <>
      <Helmet
        title={hotelName}
        link={[{ rel: 'icon', type: 'image/png', href: logoUrl }]}
      />
      <RouterProvider router={router} />
    </>
  );
};
